"use client";

import { useState, useRef, useEffect } from "react";
import {
  MoreHorizontal,
  MessageCircle,
  ThumbsDown,
  X,
  Trash,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { successToast, errorToast } from "../../utils/toasters";
import {
  addLike,
  deletePostings,
  getLikesForPost,
  getScoops,
  removeLike,
} from "../../services/scoops.js";
import followService from "../../services/follow.js";
import CommentSection from "./CommentSection.jsx";
import { getUserId, isAuthenticated } from "../../utils/helpers.js";
import { useNavigate } from "react-router-dom";
import ReportModal from "./ReportModal.jsx";
import { createReport } from "../../services/reportuser";

const SocialPostCard = ({
  post,
  postFetcher = null,
  postings = null,
  setPostings = null,
  isMobile = false,
  images = [],
  onComment,
  className = "",
  followingUsers,
  showComments = false,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [selectedReaction, setSelectedReaction] = useState(
    (post.userLikes && post.userLikes[0]) || null
  );
  const [scoopData, setScoopData] = useState(null);
  const [allScoops, setAllScoops] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalLikes, setTotalLikes] = useState(post.totalLikes);
  const likeButtonRef = useRef(null);
  const reactionsRef = useRef(null);
  const [reactionGroups, setReactionGroups] = useState({});
  const [commentCount, setCommentCount] = useState(0);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const commentSectionRef = useRef(null);
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [isContentLong, setIsContentLong] = useState(false);
  const contentRef = useRef(null);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showMobileOptionsMenu, setShowMobileOptionsMenu] = useState(false);
  const [showReactionDetails, setShowReactionDetails] = useState(false);
  const [activeReactionTab, setActiveReactionTab] = useState("all");
  const reactionDetailsRef = useRef(null);

  // Add state for ReportModal
  const [showReportModal, setShowReportModal] = useState(false);

  // Add state for Image Modal
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  const userId = getUserId();

  // Add refs for the options menus
  const optionsButtonRef = useRef(null);
  const mobileOptionsButtonRef = useRef(null);
  const optionsMenuRef = useRef(null);
  const mobileOptionsMenuRef = useRef(null);

  // use navigator
  const navigate = useNavigate();

  // Function to be passed to CommentSection to update comment count
  const updateCommentCount = (count) => {
    setCommentCount(count);
    setCommentsLoaded(true);
  };

  // Add image modal handlers
  const handleImageClick = (index) => {
    setModalImageIndex(index);
    setShowImageModal(true);
  };

  const handleModalPrevImage = () => {
    setModalImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleModalNextImage = () => {
    setModalImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleModalClose = () => {
    setShowImageModal(false);
  };

  // Handle keyboard navigation in modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showImageModal) return;

      if (e.key === "Escape") {
        handleModalClose();
      } else if (e.key === "ArrowLeft") {
        handleModalPrevImage();
      } else if (e.key === "ArrowRight") {
        handleModalNextImage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showImageModal, images.length]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showImageModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showImageModal]);

  // Add report handler function
  const handleReport = async (reason, action) => {
    try {
      if (!isAuthenticated()) {
        navigate("/signin");
        return;
      }
      setIsProcessing(true);

      const payload = {
        reported_user: post.userid,
        reason: reason,
        action: action,
        reported_post: post.postingid,
      };
      console.log("payload", payload);
      await createReport(payload);

      setShowOptionsMenu(false);
      setShowMobileOptionsMenu(false);
      if (postFetcher) {
        postFetcher((prev) => !prev);
      } else if (postings && setPostings) {
        const updatedPostings = postings.filter(
          (p) => p.postingid !== post.postingid
        );
        setPostings(updatedPostings);
      }
    } catch (error) {
      throw new Error(error.message);
    }
  };

  /*----------------------------------------------------__*/
  // Fetch likes for post
  async function fetchLikesForPost() {
    setIsProcessing(true);
    try {
      const likesResponse = await getLikesForPost(post.postingid);
      return likesResponse;
    } catch (error) {
      console.error("Error while fetching likes", error);
      //errorToast("Error while fetching likes");
    } finally {
      setIsProcessing(false);
    }
  }

  /*----------------------------------------------------__*/

  // Fetch all scoops data on component mount
  useEffect(() => {
    const fetchScoops = async () => {
      try {
        const scoopResponse = await getScoops();
        setAllScoops(scoopResponse);

        // Find the scoop data that matches the post's scoopname
        if (post.scoopname) {
          const matchingScoop = scoopResponse.find(
            (scoop) => scoop.scoopname === post.scoopname
          );
          if (matchingScoop) {
            setScoopData(matchingScoop);
          }
        }

        // Process reactions if post has likes
        if (post.likes && Array.isArray(post.likes)) {
          setReactionGroups(processReactions(post.likes));
        }
      } catch (error) {
        /*
          errorToast(
            "" +
              (error.response?.data?.message ?? error.data?.message ?? error),
            {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true
            }
          );
          */
        console.error("Error while prociessing reactions", error);
      } finally {
        setIsProcessing(false);
      }
    };

    if (post.scoopname) {
      fetchScoops();
    }
  }, [post.scoopname, post.likes]);

  // Close reactions panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        reactionsRef.current &&
        !reactionsRef.current.contains(event.target) &&
        likeButtonRef.current &&
        !likeButtonRef.current.contains(event.target)
      ) {
        setShowReactions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Add this useEffect to check content length after component mounts
  useEffect(() => {
    if (contentRef.current) {
      // For mobile view, check if content is taller than 60px (matches your current h-[60px])
      // For desktop view, we'll use a different threshold
      const contentHeight = contentRef.current.scrollHeight;
      const containerHeight = isMobile ? 60 : 72; // 3 lines of text approx for desktop

      // Only set content as long if it exceeds the container height
      setIsContentLong(contentHeight > containerHeight);
    }
  }, [post.contentinfo, isMobile, isContentExpanded]);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev < images.length - 1 ? prev + 1 : prev
    );
  };

  const [likeClick, setLikeClick] = useState(false);

  const handleNahPassClick = async () => {
    if (!isAuthenticated()) {
      localStorage.setItem("target", window.location.pathname);
      navigate("/signin");
      return;
    }
    const payload = { postid: post.postingid, emojiid: 41 };
    try {
      const previousReaction = selectedReaction;
      await addLike(payload);
      if (!selectedReaction) {
        setTotalLikes((prev) => prev + 1);
      }
      setLikeClick((prev) => !prev);
      const emoji = { emojiid: 41, title: "nah Pass", emoji: "👎" };
      const reaction = emoji;
      setSelectedReaction(emoji);
      setReactionGroups((prevGroups) => {
        const updatedGroups = { ...prevGroups };
        const currentUsername = localStorage.getItem("username") || "You";

        if (previousReaction && previousReaction.emoji) {
          if (updatedGroups[previousReaction.emoji]) {
            updatedGroups[previousReaction.emoji].count -= 1;
            updatedGroups[previousReaction.emoji].users = updatedGroups[
              previousReaction.emoji
            ].users.filter((username) => username !== currentUsername);

            // Remove the emoji group if count is zero
            if (updatedGroups[previousReaction.emoji].count <= 0) {
              delete updatedGroups[previousReaction.emoji];
            }
          }
        }

        // Add the new reaction to the groups
        if (!updatedGroups[reaction.emoji]) {
          updatedGroups[reaction.emoji] = {
            count: 1,
            users: [currentUsername],
          };
        } else {
          // Increment count if the emoji group already exists
          updatedGroups[reaction.emoji].count += 1;

          // Add user to the users list if not already there
          if (!updatedGroups[reaction.emoji].users.includes(currentUsername)) {
            updatedGroups[reaction.emoji].users.push(currentUsername);
          }
        }

        return updatedGroups;
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ??
        error.data?.message ??
        error.message ??
        error;
      console.error("error while adding nahpass: ", errorMessage);
    }
  };

  const onLike = async (postid, emoji) => {
    if (!isAuthenticated()) {
      localStorage.setItem("target", window.location.pathname);
      navigate("/signin");
      return;
    }
    const payload = { postid: postid, emojiid: emoji.emojiid };
    try {
      await addLike(payload);
      if (!selectedReaction) {
        setTotalLikes((prev) => prev + 1);
      }
      setLikeClick((prev) => !prev);
      setSelectedReaction(emoji);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ??
        error.data?.message ??
        error.message ??
        error;

      if (!localStorage.getItem("token")) {
        window.location.href = "/signin";
      }
      errorToast("Error While Adding Like");
    }
  };
  const onRemoveLike = async (postid) => {
    const payload = { postid: postid };
    try {
      await removeLike(payload);
      setTotalLikes((prev) => {
        if (prev > 1) {
          return prev - 1;
        }
        return 0;
      });
      setLikeClick((prev) => !prev);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ??
        error.data?.message ??
        error.message ??
        error;

      errorToast("Error While Removing Like");
    }
  };

  const handleLikeHover = () => {
    setShowReactions(true);
  };

  const handleLikeLeave = () => {
    // Don't hide immediately to allow clicking on reactions
    setTimeout(() => {
      if (!reactionsRef.current?.matches(":hover")) {
        setShowReactions(false);
      }
    }, 300);
  };

  const handleReactionSelect = async (reaction) => {
    // Store the previous reaction for potential removal
    const previousReaction = selectedReaction;

    // Check if clicking the same reaction (toggle off)
    if (
      isLiked &&
      selectedReaction &&
      selectedReaction.emojiid === reaction.emojiid
    ) {
      // Update UI immediately before API call
      setSelectedReaction(null);
      setIsLiked(false);

      // Remove this reaction from the groups if it was the user's only contribution
      setReactionGroups((prevGroups) => {
        const updatedGroups = { ...prevGroups };
        if (updatedGroups[reaction.emoji]) {
          // Decrease count
          updatedGroups[reaction.emoji].count -= 1;

          // Remove current user from users list
          const currentUsername = localStorage.getItem("username") || "";
          updatedGroups[reaction.emoji].users = updatedGroups[
            reaction.emoji
          ].users.filter((username) => username !== currentUsername);

          // Remove the emoji group if count is zero
          if (updatedGroups[reaction.emoji].count <= 0) {
            delete updatedGroups[reaction.emoji];
          }
        }
        return updatedGroups;
      });

      // Call API to remove like
      await onRemoveLike(post.postingid);
      return;
    }

    // Set the new reaction immediately for responsive UI
    setSelectedReaction(reaction);
    setIsLiked(true);

    // Update the reaction groups immediately
    setReactionGroups((prevGroups) => {
      const updatedGroups = { ...prevGroups };
      const currentUsername = localStorage.getItem("username") || "You";

      // If user had a previous reaction, remove it from that group
      if (previousReaction && previousReaction.emoji) {
        if (updatedGroups[previousReaction.emoji]) {
          updatedGroups[previousReaction.emoji].count -= 1;
          updatedGroups[previousReaction.emoji].users = updatedGroups[
            previousReaction.emoji
          ].users.filter((username) => username !== currentUsername);

          // Remove the emoji group if count is zero
          if (updatedGroups[previousReaction.emoji].count <= 0) {
            delete updatedGroups[previousReaction.emoji];
          }
        }
      }

      // Add the new reaction to the groups
      if (!updatedGroups[reaction.emoji]) {
        updatedGroups[reaction.emoji] = {
          count: 1,
          users: [currentUsername],
        };
      } else {
        // Increment count if the emoji group already exists
        updatedGroups[reaction.emoji].count += 1;

        // Add user to the users list if not already there
        if (!updatedGroups[reaction.emoji].users.includes(currentUsername)) {
          updatedGroups[reaction.emoji].users.push(currentUsername);
        }
      }

      return updatedGroups;
    });

    // Hide reactions panel
    setShowReactions(false);

    // Call API to add like
    await onLike(post.postingid, reaction);
  };

  const handleLikeClick = async () => {
    if (selectedReaction) {
      // Update UI immediately
      const reactionToRemove = selectedReaction;
      setSelectedReaction(null);
      setIsLiked(false);

      // Update reaction groups
      setReactionGroups((prevGroups) => {
        const updatedGroups = { ...prevGroups };
        if (updatedGroups[reactionToRemove.emoji]) {
          // Decrease count
          updatedGroups[reactionToRemove.emoji].count -= 1;

          // Remove current user from users list
          const currentUsername = localStorage.getItem("username") || "You";
          updatedGroups[reactionToRemove.emoji].users = updatedGroups[
            reactionToRemove.emoji
          ].users.filter((username) => username !== currentUsername);

          // Remove the emoji group if count is zero
          if (updatedGroups[reactionToRemove.emoji].count <= 0) {
            delete updatedGroups[reactionToRemove.emoji];
          }
        }
        return updatedGroups;
      });

      // Call API
      await onRemoveLike(post.postingid);
    } else if (scoopData && scoopData.emojis && scoopData.emojis.length > 0) {
      // Default to first emoji when clicking like button directly
      const defaultReaction = scoopData.emojis[0];
      setSelectedReaction(defaultReaction);
      setIsLiked(true);

      // Update reaction groups
      setReactionGroups((prevGroups) => {
        const updatedGroups = { ...prevGroups };
        const currentUsername = localStorage.getItem("username") || "You";

        if (!updatedGroups[defaultReaction.emoji]) {
          updatedGroups[defaultReaction.emoji] = {
            count: 1,
            users: [currentUsername],
          };
        } else {
          // Increment count
          updatedGroups[defaultReaction.emoji].count += 1;

          // Add user to the users list if not already there
          if (
            !updatedGroups[defaultReaction.emoji].users.includes(
              currentUsername
            )
          ) {
            updatedGroups[defaultReaction.emoji].users.push(currentUsername);
          }
        }

        return updatedGroups;
      });

      // Call API
      await onLike(post.postingid, defaultReaction);
    }
  };

  // Function to calculate time ago from post creation date
  const getTimeAgo = (createdDate) => {
    if (!createdDate) return "";

    const created = new Date(createdDate);
    const now = new Date();
    const diffInSeconds = Math.floor((now - created) / 1000);

    const formatTime = (date) => {
      return date.toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    };

    // Just now (less than a minute)
    if (diffInSeconds < 60) {
      return "Just now";
    }

    // Minutes (less than an hour)
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }

    // Hours (less than a day)
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    }

    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (
      created.getDate() === yesterday.getDate() &&
      created.getMonth() === yesterday.getMonth() &&
      created.getFullYear() === yesterday.getFullYear()
    ) {
      return `Yesterday at ${formatTime(created)}`;
    }

    // This week (less than 7 days)
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const dayOfWeek = dayNames[created.getDay()];
      return `${dayOfWeek} at ${formatTime(created)}`;
    }

    // Format month and day
    const month = created.toLocaleString("en-US", { month: "long" });
    const day = created.getDate();
    if (created.getFullYear() !== now.getFullYear()) {
      return `${month} ${day}, ${created.getFullYear()}`;
    }
    return `${month} ${day} at ${formatTime(created)}`;
  };
  const timeAgo = getTimeAgo(post.createdate);

  const [following, setFollowing] = useState(false);

  useEffect(() => {
    async function checkFollowingStatus() {
      if (!followingUsers) return;
      if (followingUsers.includes(post.userid)) {
        setFollowing(true);
      }
    }
    checkFollowingStatus();
  }, []);
  const handleFollowUser = async () => {
    try {
      setIsProcessing(true);
      if (!isAuthenticated()) {
        errorToast("Please Login Before You Start Following.");
        return;
      }
      await followService.handleFollowUser(post.userid);
      setFollowing(true);
      successToast("Neighbor Followed Successfully");
    } catch (error) {
      errorToast("Error while following user");
    } finally {
      setIsProcessing(false);
      setShowOptionsMenu(false);
      setShowMobileOptionsMenu(false);
    }
  };

  const handleUnfollowUser = async () => {
    try {
      setIsProcessing(true);
      await followService.handleUnfollowUser(post.userid);
      setFollowing(false);
      successToast("Neighbor Unfollowed Successfully");
    } catch (error) {
      console.error("folliwing user error", error);
      errorToast("Error while unfollowing user");
    } finally {
      setIsProcessing(false);
      setShowOptionsMenu(false);
      setShowMobileOptionsMenu(false);
    }
  };

  async function handleDeletePosting(posting) {
    try {
      setIsProcessing(true);
      await deletePostings({ postingid: posting.postingid });
      if (postFetcher) {
        postFetcher((prev) => !prev);
      }
      if (postings && setPostings) {
        const tempPostings = postings.filter(
          (p) => p.postingid != posting.postingid
        );
        setPostings(tempPostings);
      }
      setShowOptionsMenu(false);
      setShowMobileOptionsMenu(false);

      successToast("Post Deleted Successfully.");
    } catch (error) {
      console.error("Error while trying to delete post: ", error);
      errorToast("Error while trying to delete post.");
    } finally {
      setIsProcessing(false);
    }
  }

  // PaginationDots component embedded in the same file
  const PaginationDots = ({
    totalDots = 3,
    activeDot = 0,
    onDotClick,
    className = "",
  }) => {
    return (
      <div
        className={`flex items-center justify-center space-x-2 ${className}font-afacad`}
      >
        {Array.from({ length: totalDots }).map((_, index) => (
          <button
            key={index}
            onClick={() => onDotClick(index)}
            className="focus:outline-none"
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === activeDot ? "true" : "false"}
          >
            <div
              className={`
              ${
                index === activeDot
                  ? "w-4 h-4 bg-white"
                  : "w-2 h-2 bg-[#B6B3BB]"
              } 
              rounded-full transition-all duration-300
            `}
            />
          </button>
        ))}
      </div>
    );
  };

  // Image Modal Component
  const ImageModal = () => {
    if (!showImageModal || !images || images.length === 0) return null;

    return (
      <div
        className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
        onClick={handleModalClose}
      >
        {/* Close button */}
        <button
          onClick={handleModalClose}
          className={`absolute ${
            isMobile ? "top-2 right-2" : "top-4 right-4"
          } z-60 text-white hover:text-gray-300 transition-colors`}
          aria-label="Close modal"
        >
          <X className={`${isMobile ? "w-6 h-6" : "w-8 h-8"}`} />
        </button>

        {/* Image counter */}
        {images.length > 1 && (
          <div
            className={`absolute ${
              isMobile ? "top-2 left-1/2" : "top-4 left-1/2"
            } transform -translate-x-1/2 z-60 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full`}
          >
            {modalImageIndex + 1} / {images.length}
          </div>
        )}

        {/* Navigation buttons */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleModalPrevImage();
              }}
              className={`absolute ${
                isMobile ? "left-3 top-1/2" : "left-4 top-1/2"
              }
        transform -translate-y-1/2
        z-[9999]
        bg-white/30 backdrop-blur-md
        rounded-full
        p-2
        hover:bg-white/40
        transition-colors`}
              aria-label="Previous image"
            >
              <ChevronLeft className={`${isMobile ? "w-6 h-6" : "w-8 h-8"}`} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleModalNextImage();
              }}
              className={`absolute ${
                isMobile ? "right-3 top-1/2" : "right-4 top-1/2"
              }
        transform -translate-y-1/2
        z-[9999]
        bg-white/30 backdrop-blur-md
        rounded-full
        p-2
        hover:bg-white/40
        transition-colors`}
              aria-label="Next image"
            >
              <ChevronRight className={`${isMobile ? "w-6 h-6" : "w-8 h-8"}`} />
            </button>
          </>
        )}

        {/* Main image */}
        <div
          className={`relative ${
            isMobile ? "max-w-[95vw] max-h-[85vh]" : "max-w-[90vw] max-h-[90vh]"
          } flex items-center justify-center`}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={images[modalImageIndex] || "/placeholder.svg"}
            alt={`Post image ${modalImageIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg"
            style={{
              maxHeight: isMobile ? "85vh" : "90vh",
              maxWidth: isMobile ? "95vw" : "90vw",
            }}
          />
        </div>

        {/* Thumbnail navigation for multiple images */}
        {images.length > 1 && (
          <div
            className={`absolute ${
              isMobile ? "bottom-2" : "bottom-4"
            } left-1/2 transform -translate-x-1/2 z-60 flex space-x-2 ${
              isMobile ? "max-w-[95vw]" : "max-w-[90vw]"
            } overflow-x-auto px-4`}
          >
            {images.map((image, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setModalImageIndex(index);
                }}
                className={`flex-shrink-0 ${
                  isMobile ? "w-12 h-12" : "w-16 h-16"
                } rounded-lg overflow-hidden border-2 transition-all ${
                  index === modalImageIndex
                    ? "border-white opacity-100"
                    : "border-transparent opacity-60 hover:opacity-80"
                }`}
              >
                <img
                  src={image || "/placeholder.svg"}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Reactions panel component
  const ReactionsPanel = ({ scoopData }) => {
    if (!scoopData || !scoopData.emojis) return null;

    return (
      <div
        ref={reactionsRef}
        className="absolute bottom-full left-0 mb-2 bg-white rounded-full shadow-lg px-2 py-1 flex items-center transition-all duration-300 z-20"
        onMouseEnter={() => setShowReactions(true)}
        onMouseLeave={() => setShowReactions(false)}
      >
        {scoopData.emojis.map((reaction) => (
          <button
            key={reaction.emojiid}
            className="hover:scale-125 transition-transform duration-200 px-2 py-1 relative group"
            onClick={() => handleReactionSelect(reaction)}
            aria-label={reaction.title}
          >
            <span className="text-xl">{reaction.emoji}</span>
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 whitespace-nowrap">
              {reaction.title}
            </span>
          </button>
        ))}
      </div>
    );
  };

  // Function to process reactions and group them by emoji type
  const processReactions = (likes) => {
    if (!likes || !Array.isArray(likes)) return {};

    const groups = {};
    likes.forEach((like) => {
      if (like.emoji) {
        if (!groups[like.emoji]) {
          groups[like.emoji] = {
            count: 1,
            users: [""],
          };
        } else {
          groups[like.emoji].count += 1;
          if (like.username) {
            groups[like.emoji].users.push(like.username);
          }
        }
      }
    });

    return groups;
  };

  const handleLikesCountClick = () => {
    if (Object.keys(reactionGroups).length > 0) {
      setShowReactionDetails(true);
      // Set "all" as the default active tab
      setActiveReactionTab("all");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        reactionDetailsRef.current &&
        !reactionDetailsRef.current.contains(event.target)
      ) {
        setShowReactionDetails(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showReactionDetails]);

  const [allReactedUsers, setAllReactedUsers] = useState(null);

  useEffect(() => {
    async function fetcherLikesForPost() {
      const likesResponse = await fetchLikesForPost();
      const allUsers = likesResponse.data.likes;
      setAllReactedUsers(allUsers);
    }
    fetcherLikesForPost();
  }, [likeClick]);

  // Reaction Details Popup component
  const ReactionDetailsPopup = ({ reactionGroups }) => {
    if (!reactionGroups) return null;

    // Flatten all users into a single array for the "all" view
    const allUsers = allReactedUsers;
    if (!allUsers) {
      return;
    }
    // Get total reactions count across all types
    const totalReactions = Object.values(allUsers).reduce(
      (sum, group) => sum + group.count,
      0
    );

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowReactionDetails(false);
          }
        }}
      >
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-semibold">Vibes</h2>
            <button
              onClick={() => setShowReactionDetails(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors rounded-full p-1 hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs for reaction types */}
          <div className="flex border-b border-gray-200">
            <button
              className={`flex-1 py-3 px-2 text-center transition-all duration-200 ${
                activeReactionTab === "all"
                  ? "border-b-2 border-black font-medium"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setActiveReactionTab("all")}
            >
              All
              <span className="ml-1 text-xs bg-gray-100 px-1.5 py-0.5 rounded-full">
                {totalReactions}
              </span>
            </button>

            {allUsers &&
              allUsers.map((data) => (
                <button
                  key={data.emoji}
                  className={`flex-1 py-3 px-2 text-center transition-all duration-200 flex flex-col items-center ${
                    activeReactionTab === data.emoji
                      ? "border-b-2 border-black font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                  onClick={() => setActiveReactionTab(data.emoji)}
                >
                  <span className="text-lg block">{data.emoji}</span>
                  <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded-full">
                    {data.count}
                  </span>
                </button>
              ))}
          </div>

          {/* User list */}
          <div className="overflow-y-auto flex-grow custom-scrollbar">
            {allUsers &&
              (activeReactionTab === "all"
                ? allUsers.flatMap((group) => group.data)
                : allUsers.find((group) => group.emoji === activeReactionTab)
                    ?.data || []
              )?.map((user) => (
                <div
                  key={user.userid}
                  className="flex items-center p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="relative mr-3 flex-shrink-0">
                    {/* Avatar */}
                    <div className="w-14 h-14 rounded-full overflow-hidden border border-gray-200">
                      <img
                        src={user.avatarurl || "/placeholder.svg"}
                        alt={user.username}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Reaction emoji with circular background at bottom left */}
                    <div className="absolute -bottom-1 -left-1 bg-white rounded-full border border-gray-200 w-6 h-6 flex items-center justify-center shadow-sm">
                      <span className="text-sm">
                        {activeReactionTab === "all"
                          ? allUsers.find((group) =>
                              group.data.some((u) => u.userid === user.userid)
                            )?.emoji || "🤔"
                          : activeReactionTab}
                      </span>
                    </div>
                  </div>

                  <div className="flex-grow">
                    <div className="flex items-center">
                      <h3 className="font-semibold text-gray-900">
                        {user.username}
                      </h3>
                    </div>
                    {/* <p className="text-sm text-gray-600 mt-0.5">{group.title}</p> */}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };

  // Add this CSS class for custom scrollbar styling
  // Add to the top of the file, after the imports
  useEffect(() => {
    // Add custom scrollbar styles
    const style = document.createElement("style");
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #a1a1a1;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const ReactionBubbles = ({ reactionGroups }) => {
    if (!reactionGroups || Object.keys(reactionGroups).length === 0)
      return null;

    return (
      <div className="relative">
        <div
          onClick={handleLikesCountClick}
          className="flex -space-x-1 overflow-hidden cursor-pointer group"
        >
          {Object.entries(reactionGroups).map(([emoji, data], index) => (
            <div
              key={index}
              className="inline-block h-6 w-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-sm shadow-sm hover:scale-110 transition-transform"
              title={`${data.users.slice(0, 3).join(", ")}${
                data.users.length > 3
                  ? ` and ${data.users.length - 3} others`
                  : ""
              }`}
            >
              {emoji}
            </div>
          ))}
        </div>

        {showReactionDetails && (
          <ReactionDetailsPopup reactionGroups={reactionGroups} />
        )}
      </div>
    );
  };

  // Add a function to toggle content expansion
  const toggleContentExpansion = () => {
    setIsContentExpanded(!isContentExpanded);
  };

  const toggleComments = () => {
    setShowComments((prev) => !prev);
  };

  // Add this useEffect to close the options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // For desktop options menu
      if (
        showOptionsMenu &&
        optionsMenuRef.current &&
        !optionsMenuRef.current.contains(event.target) &&
        optionsButtonRef.current &&
        !optionsButtonRef.current.contains(event.target)
      ) {
        setShowOptionsMenu(false);
      }

      // For mobile options menu
      if (
        showMobileOptionsMenu &&
        mobileOptionsMenuRef.current &&
        !mobileOptionsMenuRef.current.contains(event.target) &&
        mobileOptionsButtonRef.current &&
        !mobileOptionsButtonRef.current.contains(event.target)
      ) {
        setShowMobileOptionsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOptionsMenu, showMobileOptionsMenu]);

  const [showCommentsState, setShowComments] = useState(showComments);

  if (isMobile) {
    return (
      <div className="bg-white rounded-lg shadow-sm w-full flex-shrink-0">
        <div className="p-4">
          {/* Header */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center">
              <div className="h-10 w-10 bg-gray-200 rounded-full mr-3 overflow-hidden">
                {post && post.avatar_url ? (
                  <img
                    src={post.avatar_url || "/placeholder.svg"}
                    alt={post.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={"/placeholder.svg"}
                    alt={post.username}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div>
                <div className="font-medium">{post.username}</div>
                <div className="text-xs text-gray-500">
                  {post.username} · {timeAgo}
                </div>
              </div>
            </div>
            <div className="relative">
              <button
                ref={mobileOptionsButtonRef}
                className="text-gray-400"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMobileOptionsMenu(!showMobileOptionsMenu);
                }}
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
              {showMobileOptionsMenu && (
                <div
                  ref={mobileOptionsMenuRef}
                  className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200"
                >
                  {post.userid != userId ? (
                    <>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        onClick={() => {
                          // Handle follow action
                          if (following) {
                            handleUnfollowUser();
                          } else {
                            handleFollowUser();
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                          />
                        </svg>
                        {following ? "UnFollow neighbor" : "Follow neighbor"}
                      </button>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                        onClick={() => {
                          setShowReportModal(true);
                          setShowMobileOptionsMenu(false);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        Report
                      </button>
                    </>
                  ) : (
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      disabled={isProcessing}
                      onClick={() => {
                        handleDeletePosting(post);
                      }}
                    >
                      {isProcessing && <Loader2 className="animate-spin" />}
                      {!isProcessing && (
                        <>
                          <Trash className="mr-2" />
                          Delete Post
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Image Carousel */}
          {images && images.length > 0 && (
            <div className="relative flex-grow mb-4">
              <div className="w-full h-full relative rounded-lg bg-white overflow-hidden">
                <div className="w-full h-full rounded-lg overflow-hidden">
                  <div className="w-full h-full rounded-lg overflow-hidden">
                    <img
                      src={images[currentImageIndex] || "/placeholder.svg"}
                      alt="Post image"
                      className="w-full h-auto object-contain cursor-pointer"
                      onClick={() => handleImageClick(currentImageIndex)}
                    />
                  </div>
                </div>
              </div>
              {images.length > 1 && (
                <>
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                    <button
                      onClick={handlePrevImage}
                      disabled={currentImageIndex === 0}
                      className="disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                      aria-label="Previous image"
                    >
                      <img
                        src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Frame%201171279062-vnAwUYEvftwRA2jmHffSdx5SwWvC3I.svg"
                        alt="Previous"
                        width="24"
                        height="24"
                      />
                    </button>
                  </div>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 z-10">
                    <button
                      onClick={handleNextImage}
                      disabled={currentImageIndex === images.length - 1}
                      className="disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                      aria-label="Next image"
                    >
                      <img
                        src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Frame%201171279061-n6vOnZLf3AKojY7n8t2UlShZOLgyyP.svg"
                        alt="Next"
                        width="24"
                        height="24"
                      />
                    </button>
                  </div>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                    <PaginationDots
                      totalDots={images.length}
                      activeDot={currentImageIndex}
                      onDotClick={setCurrentImageIndex}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Category and Hashtags */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {post.scoopname && (
              <button className="bg-black text-white font-medium text-sm rounded-full border shadow-md px-4 py-1.5">
                {post.scoopname}
              </button>
            )}
            {post.hoops &&
              post.hoops.length > 0 &&
              post.hoops.map((tag, index) => (
                <span
                  key={index}
                  className="bg-fuchsia-100 text-fuchsia-800 text-xs px-3 py-1 rounded-full font-medium"
                >
                  {tag.hooptag}
                </span>
              ))}
          </div>

          {/* Content */}
          <div className="mb-3">
            <p
              ref={contentRef}
              className={`text-sm text-gray-800 ${
                isContentExpanded ? "" : "line-clamp-3"
              }`}
            >
              {post.contentinfo}
            </p>
            {isContentLong && (
              <button
                className="text-sm text-gray-800 font-medium italic border-b border-gray-800 pb-0.5 inline-block focus:outline-none mt-1"
                onClick={toggleContentExpansion}
              >
                {isContentExpanded ? "Show less" : "Read more"}
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <button
                  ref={likeButtonRef}
                  className="flex items-center text-gray-500"
                  onMouseEnter={handleLikeHover}
                  onMouseLeave={handleLikeLeave}
                  onClick={handleLikeClick}
                >
                  {selectedReaction ? (
                    <span className="text-xl mr-1">
                      {selectedReaction.emoji}
                    </span>
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 22H4C3.46957 22 2.96086 21.7893 2.58579 21.4142C2.21071 21.0391 2 20.5304 2 20V13C2 12.4696 2.21071 11.9609 2.58579 11.5858C2.96086 11.2107 3.46957 11 4 11H7M14 9V5C14 4.20435 13.6839 3.44129 13.1213 2.87868C12.5587 2.31607 11.7956 2 11 2L7 11V22H18.28C18.7623 22.0055 19.2304 21.8364 19.5979 21.524C19.9654 21.2116 20.2077 20.7769 20.28 20.3L21.66 11.3C21.7035 11.0134 21.6842 10.7207 21.6033 10.4423C21.5225 10.1638 21.3821 9.90629 21.1919 9.68751C21.0016 9.46873 20.7661 9.29393 20.5016 9.17522C20.2371 9.0565 19.9499 8.99672 19.66 9H14Z"
                        stroke={isLiked ? "#FF0000" : "#000000"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>

                {showReactions && scoopData && (
                  <ReactionsPanel scoopData={scoopData} />
                )}
              </div>
              <div className="flex items-center ml-1">
                <ReactionBubbles reactionGroups={reactionGroups} />
                <span
                  className="ml-1 text-sm cursor-pointer hover:underline"
                  onClick={handleLikesCountClick}
                >
                  {totalLikes}
                </span>
              </div>
              <button
                onClick={toggleComments}
                className="flex items-center text-gray-500"
              >
                <MessageCircle className="h-5 w-5 mr-1" />
                <span>
                  {commentCount} {commentCount === 1 ? "comment" : "comments"}
                </span>
              </button>
            </div>
            <button
              onClick={handleNahPassClick}
              className="flex cursor-pointer  items-center text-gray-500 text-xs"
            >
              <ThumbsDown className="h-4 w-4 mr-1" />
              Nahh! Pass
            </button>
          </div>

          {/* Always render CommentSection but keep it hidden when not showing comments */}
          <div
            className={!commentsLoaded ? "hidden" : ""}
            style={{ display: "none" }}
          >
            <CommentSection
              scoopid={post.scoopid}
              onCommentCountChange={updateCommentCount}
              autoFetchCount={true}
              ref={commentSectionRef}
            />
          </div>

          {/* Visible CommentSection when showComments is true */}
          <div style={{ display: showCommentsState ? "block" : "none" }}>
            <CommentSection
              onCommentCountChange={updateCommentCount}
              postid={post.postingid}
              scoopid={post.scoopid}
              autoFetchCount={!commentsLoaded}
              ref={commentSectionRef}
            />
          </div>
        </div>

        {/* Image Modal */}
        <ImageModal />

        {/* Report Modal */}
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          onReport={handleReport}
          username={post.username}
        />
      </div>
    );
  }

  return (
    post && (
      <div
        className={`bg-white border rounded-xl shadow-sm overflow-hidden h-full mb-6 flex flex-col ${className}`}
      >
        {/* Header */}
        <div className="flex items-center p-4">
          <div className="h-10 w-10 rounded-full overflow-hidden relative">
            {post && post?.avatar_url ? (
              <img
                src={post.avatar_url || "/placeholder.svg"}
                alt={post.username}
                className="w-full h-full object-cover"
                style={{ position: "absolute", top: 0, left: 0 }}
              />
            ) : (
              <img
                src={"/placeholder.svg"}
                alt={post.username}
                className="w-full h-full object-cover"
                style={{ position: "absolute", top: 0, left: 0 }}
              />
            )}
          </div>
          <div className="ml-3 flex-1">
            <div className="flex items-center">
              <span className="font-semibold text-sm">{post.username}</span>
            </div>
            <div className="text-gray-500 text-xs flex items-center">
              <span>@{post.username}</span>
              <span className="mx-1">•</span>
              <span>{timeAgo}</span>
            </div>
          </div>
          <div className="relative">
            <button
              ref={optionsButtonRef}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Menu"
              onClick={(e) => {
                e.stopPropagation();
                setShowOptionsMenu(!showOptionsMenu);
              }}
            >
              <img
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Frame-rFDa78aaT1Ml5vtnRtezN8VGFBedhm.svg"
                alt="Menu"
                width="20"
                height="20"
              />
            </button>
            {showOptionsMenu && (
              <div
                ref={optionsMenuRef}
                className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200"
              >
                {post.userid != userId ? (
                  <>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      onClick={() => {
                        // Handle follow action
                        if (following) {
                          handleUnfollowUser();
                        } else {
                          handleFollowUser();
                        }
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                        />
                      </svg>

                      {following ? "UnFollow neighbor" : "Follow neighbor"}
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      onClick={() => {
                        // Handle share action
                        successToast("Share option clicked");
                        setShowOptionsMenu(false);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                        />
                      </svg>
                      Share
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                      onClick={() => {
                        setShowReportModal(true);
                        setShowOptionsMenu(false);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      Report
                    </button>
                  </>
                ) : (
                  <button
                    className="w-full hover:bg-red-500 hover:text-white text-left px-4 py-2 text-sm text-gray-700  flex items-center"
                    disabled={isProcessing}
                    onClick={() => {
                      handleDeletePosting(post);
                    }}
                  >
                    {isProcessing && <Loader2 className="animate-spin" />}
                    {!isProcessing && (
                      <>
                        <Trash className="mr-2" />
                        Delete Posting
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Category and Hashtags */}
        <div className="px-4 pt-0 flex flex-wrap items-center gap-2 mb-2">
          {post.scoopname && (
            <button className="bg-black text-white font-medium text-sm rounded-full border shadow-md px-4 py-1.5">
              {post.scoopname}
            </button>
          )}
          {post &&
            post.hoops &&
            post.hoops.length > 0 &&
            post.hoops.map((tag, index) => (
              <span
                key={index}
                className="bg-fuchsia-100 text-fuchsia-800 text-xs px-3 py-1 rounded-full font-medium"
              >
                {tag.hooptag}
              </span>
            ))}
        </div>
        {/* Image Carousel */}
        {images && images.length > 0 && (
          <div className="relative mb-4">
            <div className="w-full relative  bg-pink-50 overflow-hidden">
              {/* Dynamic container that adapts to image aspect ratio */}
              <div className="relative w-full">
                <img
                  src={images[currentImageIndex] || "/placeholder.svg"}
                  alt="Post image"
                  className="w-full h-auto max-h-[600px] object-contain cursor-pointer"
                  onClick={() => handleImageClick(currentImageIndex)}
                  style={{
                    minHeight: "200px", // Minimum height for very wide images
                    maxWidth: "100%",
                  }}
                  onLoad={(e) => {
                    // Optional: Add loading state management here
                    const img = e.target;
                    const aspectRatio = img.naturalWidth / img.naturalHeight;

                    // Adjust container for extreme aspect ratios
                    if (aspectRatio > 3) {
                      // Very wide images - limit height
                      img.style.maxHeight = "300px";
                    } else if (aspectRatio < 0.5) {
                      // Very tall images - limit height more
                      img.style.maxHeight = "500px";
                    }
                  }}
                />
              </div>
            </div>

            {/* Image Navigation */}
            {images.length > 1 && (
              <>
                {/* Previous Button */}
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                  <button
                    onClick={handlePrevImage}
                    disabled={currentImageIndex === 0}
                    className="bg-black/60 hover:bg-black/80 disabled:opacity-30 disabled:cursor-not-allowed 
                     rounded-full p-2 transition-all duration-200 focus:outline-none focus:ring-2 
                     focus:ring-white/50 backdrop-blur-sm"
                    aria-label="Previous image"
                  >
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                </div>

                {/* Next Button */}
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10">
                  <button
                    onClick={handleNextImage}
                    disabled={currentImageIndex === images.length - 1}
                    className="bg-black/60 hover:bg-black/80 disabled:opacity-30 disabled:cursor-not-allowed 
                     rounded-full p-2 transition-all duration-200 focus:outline-none focus:ring-2 
                     focus:ring-white/50 backdrop-blur-sm"
                    aria-label="Next image"
                  >
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>

                {/* Pagination Dots */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                  <PaginationDots
                    totalDots={images.length}
                    activeDot={currentImageIndex}
                    onDotClick={setCurrentImageIndex}
                  />
                </div>

                {/* Image Counter (Twitter-style) */}
                <div className="absolute top-4 right-4 z-10">
                  <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm font-medium">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-4 pt-2">
          <p
            ref={contentRef}
            className={`text-sm text-gray-800 mb-2 ${
              isContentExpanded ? "" : "line-clamp-3"
            }`}
          >
            {post.contentinfo}
          </p>
          {isContentLong && (
            <button
              className="text-sm text-gray-800 font-medium italic border-b border-gray-800 pb-0.5 inline-block focus:outline-none"
              onClick={toggleContentExpansion}
            >
              {isContentExpanded ? "Show less" : "Read more"}
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-gray-100 px-4 py-3 flex items-center mt-auto">
          <div className="flex items-center space-x-2">
            <div className="relative">
              <button
                ref={likeButtonRef}
                onClick={handleLikeClick}
                onMouseEnter={handleLikeHover}
                onMouseLeave={handleLikeLeave}
                className="flex items-center"
                aria-label={isLiked ? "Unlike" : "Like"}
              >
                {selectedReaction ? (
                  <span className="text-xl ">{selectedReaction.emoji}</span>
                ) : (
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 22H4C3.46957 22 2.96086 21.7893 2.58579 21.4142C2.21071 21.0391 2 20.5304 2 20V13C2 12.4696 2.21071 11.9609 2.58579 11.5858C2.96086 11.2107 3.46957 11 4 11H7M14 9V5C14 4.20435 13.6839 3.44129 13.1213 2.87868C12.5587 2.31607 11.7956 2 11 2L7 11V22H18.28C18.7623 22.0055 19.2304 21.8364 19.5979 21.524C19.9654 21.2116 20.2077 20.7769 20.28 20.3L21.66 11.3C21.7035 11.0134 21.6842 10.7207 21.6033 10.4423C21.5225 10.1638 21.3821 9.90629 21.1919 9.68751C21.0016 9.46873 20.7661 9.29393 20.5016 9.17522C20.2371 9.0565 19.9499 8.99672 19.66 9H14Z"
                      stroke={"#000000"}
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>

              {showReactions && scoopData && (
                <ReactionsPanel scoopData={scoopData} />
              )}
            </div>

            <div className="flex items-center">
              <ReactionBubbles reactionGroups={reactionGroups} />
              <span
                className="text-sm font-medium ml-1 cursor-pointer hover:underline group-hover:text-gray-700 transition-colors"
                onClick={handleLikesCountClick}
              >
                {totalLikes}
              </span>
            </div>
            <button
              onClick={toggleComments}
              className="flex items-center"
              aria-label="Comment"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="ml-1 text-sm font-medium ">{commentCount}</span>
            </button>
          </div>

          <div
            onClick={handleNahPassClick}
            className="ml-auto cursor-pointer flex items-center text-sm text-gray-500"
          >
            <img
              src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Frame%20%281%29-cLTeEyEEEt8C4SNUH7HjLQPS4H0rAo.svg"
              alt="Pass"
              width="24"
              height="24"
              className="mr-1"
            />
            <span>Nahh! Pass</span>
          </div>
        </div>

        {/* Always render CommentSection but keep it hidden when not showing comments */}
        <div
          className={!commentsLoaded ? "hidden" : ""}
          style={{ display: "none" }}
        >
          <CommentSection
            onCommentCountChange={updateCommentCount}
            scoopid={post.scoopid}
            postid={post.postingid}
            autoFetchCount={true}
            ref={commentSectionRef}
          />
        </div>

        {/* Visible CommentSection when showComments is true */}
        <div style={{ display: showCommentsState ? "block" : "none" }}>
          <CommentSection
            onCommentCountChange={updateCommentCount}
            postid={post.postingid}
            scoopid={post.scoopid}
            autoFetchCount={!commentsLoaded}
            ref={commentSectionRef}
          />
        </div>

        {/* Image Modal */}
        <ImageModal />

        {/* Report Modal */}
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          onReport={handleReport}
          username={post.username}
        />
      </div>
    )
  );
};

export default SocialPostCard;
